// frontend/src/pages/dashboard/ReviewsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  getReviews, // GET /api/reviews
  deleteReview, // DELETE /api/reviews/:id
  batchDeleteReviews, // POST /api/reviews/batch-delete { ids: [...] }
  // updateReviewStatus // optional: PATCH /api/reviews/:id/status
} from "../../services/reviewServices.js";

export default function ReviewsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busyIds, setBusyIds] = useState(new Set());

  // initial load
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getReviews();
        setRows(data); // expects array of reviews
      } catch (e) {
        setErr(e.message || "Failed to load reviews");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const allSelected = useMemo(
    () => rows.length > 0 && selected.size === rows.length,
    [rows, selected]
  );

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    setBusyIds((prev) => new Set([...prev, id]));
    try {
      await deleteReview(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    } catch (e) {
      alert(e.message || "Failed to delete review");
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected review(s)?`)) return;
    try {
      await batchDeleteReviews(ids);
      setRows((prev) => prev.filter((r) => !selected.has(r.id)));
      setSelected(new Set());
    } catch (e) {
      alert(e.message || "Failed to delete selected reviews");
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <div className="spinner-border" role="status" />
        <span className="ms-2">Loading reviews…</span>
      </div>
    );
  }

  if (err) {
    return <div className="alert alert-danger">Error: {err}</div>;
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="m-0">Reviews</h3>
        <button
          className="btn btn-outline-danger"
          onClick={deleteSelected}
          disabled={selected.size === 0}
          title={selected.size === 0 ? "Select rows first" : "Delete selected"}
        >
          Delete Selected ({selected.size})
        </button>
      </div>

      <div className="table-responsive">
        <table className="table align-middle">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                />
              </th>
              <th>ID</th>
              <th>Name</th>
              <th>Rating</th>
              <th>Message</th>
              <th>Created</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No reviews found.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                    />
                  </td>
                  <td>{r.id}</td>
                  <td>{r.display_name}</td>
                  <td>{r.rating}</td>
                  <td style={{ maxWidth: 480, whiteSpace: "pre-wrap" }}>
                    {r.message}
                  </td>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteOne(r.id)}
                      disabled={busyIds.has(r.id)}
                    >
                      {busyIds.has(r.id) ? "Deleting…" : "Delete"}
                    </button>
                    {/* Optional approve/reject actions:
                    <button
                      className="btn btn-sm btn-outline-success ms-2"
                      onClick={() => updateReviewStatus(r.id, "approved")}
                    >
                      Approve
                    </button>
                    */}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
